import { z } from '@customerio/hydra-validation';
import type { DsBrandDocumentBrandDocumentResponse } from '@customerio/hydra-ui-api';
import { defineEntity, readSchema } from '../shared/define-entity';

/**
 * Design Studio brand document entity — workspace-scoped singleton.
 *
 * A workspace has exactly one brand document (the BRAND.md markdown the agent
 * and the Global Styles page's Brand section author against). It is read off the composite GET
 * /ds/global_styles (`useDsGlobalStyles().data.brand`, which runs this entity's
 * `parse` over the envelope's `brand`) and written through the composite PUT
 * (`useUpdateDsGlobalStyles` with a `brand`), so this module carries no
 * dedicated GET/PUT cache slot — just the parse schema
 * and the generate-status poll keys. A workspace that has never saved one still
 * reads back `exists: false` (never a 404), so parse always succeeds — no null
 * tolerance needed (unlike global styles).
 *
 * **Wire shape kept as-is.** The composite GET's `brand` is fully modeled in the
 * spec (`markdown`, `max_bytes`/`max_lines` limits, provenance fields), so the
 * entity type aliases the generated model directly. The epoch-seconds timestamp
 * `generated_at` deliberately stays `number | null` — `null` means "never
 * generated", and the shared `nullableEpochSecondsToDate` transform would
 * collapse that onto epoch 0.
 *
 * **No write schema.** The brand write (`useUpdateDsGlobalStyles` in
 * `ds-global-styles/mutations`) builds its brand-only body by hand from the
 * composite request type; nothing round-trips a write through this entity.
 *
 * The generate mutation + status poll are auxiliary (AI authoring from a
 * completed styles-extract run); they return the generated wire types
 * directly, mirroring how `ds-global-styles` treats its extract/process
 * workflow reads.
 */

/* ------------------------------------------------------------------------- */
/* Wire type re-exports — the generated ui_api models                         */
/* ------------------------------------------------------------------------- */

export type {
  DsBrandDocumentBrandDocumentResponse,
  DsBrandDocumentGenerateBrandDocumentRequest,
  DsBrandDocumentGenerateBrandDocumentResponse,
  DsBrandDocumentGenerateStatusResponse,
} from '@customerio/hydra-ui-api';

/** Brand asset imported into the asset library during an authoring run. */
export type { ServicesAiEnrichmentImportedBrandAsset as DsImportedBrandAsset } from '@customerio/hydra-ui-api';

/**
 * Brand image the authoring run selected but dropped (CON-8170). The import is
 * best-effort — a drop never fails the run — so this is the only place the user
 * learns an image didn't make it. `reason` stays the wire's plain string; the
 * review UI maps the known values to copy and words anything else generically.
 */
export type { ServicesAiEnrichmentSkippedBrandAsset as DsSkippedBrandAsset } from '@customerio/hydra-ui-api';

/* ------------------------------------------------------------------------- */
/* Type                                                                       */
/* ------------------------------------------------------------------------- */

/**
 * The workspace brand document. `exists: false` marks a workspace that has
 * never saved one (the GET still returns the byte/line limits so the editor
 * can enforce them before the first save).
 */
export type DsBrandDocument = DsBrandDocumentBrandDocumentResponse;

/* ------------------------------------------------------------------------- */
/* Schema — pass-through (no transforms)                                      */
/* ------------------------------------------------------------------------- */

const DsBrandDocumentFromApi = readSchema<DsBrandDocument>(z.looseObject({}));

/* ------------------------------------------------------------------------- */
/* Entity                                                                     */
/* ------------------------------------------------------------------------- */

/** Constant pk — one brand document per workspace. */
const SINGLETON_ID = 'current';

/** @public */
export const dsBrandDocument = defineEntity<DsBrandDocument, never, never, undefined, 'workspace'>({
  name: 'ds-brand-document',
  scope: 'workspace',
  pk: () => SINGLETON_ID,
  schema: { read: DsBrandDocumentFromApi },
});

export const dsBrandDocumentKeys = {
  ...dsBrandDocument.keys,
  /**
   * Authoring-run status poll. Transient, keyed by the workflow + run ids the
   * generate mutation returns.
   */
  generateStatus: (workspaceId: number, workflowId: string, runId: string) =>
    [...dsBrandDocument.keys.all(workspaceId), 'generate-status', workflowId, runId] as const,
};
